/**
 * @file The composite sub-fields of ONE ArTable column, as the dotted
 * `<columnTypeId>.<subFieldId>` addresses a row projection may declare as target
 * columns and read back through a `sourceCell` — the exact spelling
 * `arFormulaTargetFieldSegments` writes and `readArStoredFormulaResult` reads.
 * Consumers: `formDeriveTargetUniverse`, which feeds both the target-column
 * select and the `sourceCell` select through `columnPathOptionGroup`, and
 * `formDeriveLabelResolvers` (the label a dotted target typeId renders as), so
 * the address a select OFFERS and the address a sentence NAMES are minted by one
 * body. `deriveWorkflowOptions` is the third consumer: a workflow row filter and a
 * column update name a column by the same dotted address.
 * Invariants: the enumeration is `FormulaSequenceEditor`'s, because a projection
 * column and a formula target write the same path — every sub-field of the
 * composite's default layout, with NO aggregatable filter, which is the filter
 * `formDeriveColumnValueParts` applies for the different `aggregationFieldPath`
 * position; a non-composite column yields nothing, so an unofferable address is
 * also an unnameable one.
 */
import type { CompositeFieldType, FieldType } from '#types';
import { extractFieldsFromLayout } from '@/services/editorExtractors';

export type FormDeriveColumnSubField = {
  /** The segment after the FIRST dot, exactly as the target address carries it. */
  subFieldId: string;
  /** `<columnTypeId>.<subFieldId>`, the key column-label resolution is keyed by. */
  typeId: string;
  label: string;
};

function formDeriveColumnSubFields(
  columnTypeId: string,
  fieldTypes: Record<string, FieldType>,
  compositeFieldTypes: readonly CompositeFieldType[],
): FormDeriveColumnSubField[] {
  const composite = compositeFieldTypes.find((candidate) => candidate.docId === columnTypeId);
  const structure = composite?.defaultUILayout?.structure;
  if (!structure) return [];
  return extractFieldsFromLayout(structure, fieldTypes).map((subField) => ({
    subFieldId: subField.fieldId,
    typeId: `${columnTypeId}.${subField.fieldId}`,
    label: subField.label,
  }));
}

export { formDeriveColumnSubFields };
